/**
 * Un trabajador, ya abierto. El plan lo manda la página: si se cambia en la
 * semana, aquí se ve el mismo.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useSoloLectura } from '../../auth'
import { api, ErrorApi } from '../../lib/api'
import { bajaDe, esFinIndefinido, intervaloBaja, textoBaja, type ModoBaja } from '../../lib/horarios-baja'
import { resumenPlan } from '../../lib/horarios-cobertura'
import { colorDeProfesional } from '../../lib/horarios-colores'
import { hoyISO } from '../calendario-fechas'
import {
    añadirTramo,
    franjaDeTramos,
    partirTramos,
    quitarTramo,
    tramosDeFranja,
    type Plan,
} from '../../lib/horarios-tramos'

export type { Plan }

export const DIAS: { key: keyof Plan; corto: string; label: string }[] = [
    { key: 'monday', corto: 'Lun', label: 'Lunes' },
    { key: 'tuesday', corto: 'Mar', label: 'Martes' },
    { key: 'wednesday', corto: 'Mié', label: 'Miércoles' },
    { key: 'thursday', corto: 'Jue', label: 'Jueves' },
    { key: 'friday', corto: 'Vie', label: 'Viernes' },
    { key: 'saturday', corto: 'Sáb', label: 'Sábado' },
    { key: 'sunday', corto: 'Dom', label: 'Domingo' },
]

export type Vacacion = { id: number; profesionalId: number; inicio: string; fin: string }

export function TarjetaHorario({
    accountId,
    profesional,
    plan,
    onCambiarPlan,
    vacaciones,
    colocando,
    hueco,
    totalHuecos,
    seleccionada,
    diaSeleccionado,
    onSeleccionar,
    onPreguntarAlcance,
    onGuardado,
    onSubir,
    onBajar,
}: {
    accountId: string
    profesional: { id: number; nombre: string; color?: string }
    plan: Plan
    onCambiarPlan: (plan: Plan) => void
    vacaciones: Vacacion[]
    colocando: boolean
    hueco: number
    totalHuecos: number
    seleccionada: boolean
    diaSeleccionado: keyof Plan | null
    onSeleccionar: () => void
    onPreguntarAlcance: (dia: keyof Plan, punto: { x: number; y: number }) => void
    onGuardado: () => void
    onSubir: () => void
    onBajar: () => void
}) {
    const qc = useQueryClient()
    const soloLectura = useSoloLectura()
    const [msg, setMsg] = useState<string | null>(null)
    const [vacDesde, setVacDesde] = useState('')
    const [vacHasta, setVacHasta] = useState('')
    const [modoBaja, setModoBaja] = useState<ModoBaja | null>(null)
    const [fechaBaja, setFechaBaja] = useState('')
    const [errorBaja, setErrorBaja] = useState<string | null>(null)
    const color = profesional.color ?? colorDeProfesional(profesional.id)
    const hoy = hoyISO()
    const vigente = bajaDe(vacaciones, profesional.id, hoy)

    const guardar = useMutation({
        mutationFn: () =>
            api.put(`/business/${accountId}/agenda/profesionales/${profesional.id}/horario`, { plan }),
        onSuccess: async () => {
            setMsg('Horario guardado')
            onGuardado()
            await qc.invalidateQueries({ queryKey: ['horarios', accountId] })
        },
        onError: (e) => setMsg(e instanceof ErrorApi ? e.message : 'Error al guardar'),
    })

    const [avisoCitas, setAvisoCitas] = useState<number | null>(null)

    const altaVac = useMutation({
        mutationFn: (cuerpo: { inicio: string; fin: string; confirmarConCitas?: boolean }) =>
            api.post(`/business/${accountId}/agenda/vacaciones`, {
                profesionalId: profesional.id,
                ...cuerpo,
            }),
        onSuccess: async () => {
            setVacDesde('')
            setVacHasta('')
            setModoBaja(null)
            setFechaBaja('')
            setErrorBaja(null)
            setAvisoCitas(null)
            await qc.invalidateQueries({ queryKey: ['vacaciones', accountId] })
        },
        onError: (e) => {
            if (e instanceof ErrorApi && e.status === 409) {
                const c = e.cuerpo as { motivo?: string; citas?: { id: number }[] } | undefined
                if (c?.motivo === 'citas_encima') {
                    const n = c.citas?.length ?? 0
                    setAvisoCitas(n)
                    setErrorBaja(
                        `Hay ${n} ${n === 1 ? 'cita' : 'citas'} encima. No se reagendan. Pulsa otra vez para anular el día igual.`,
                    )
                    return
                }
            }
            setErrorBaja(e instanceof ErrorApi ? e.message : 'No se pudo anular')
        },
    })

    const borrarVac = useMutation({
        mutationFn: (id: number) => api.delete(`/business/${accountId}/agenda/vacaciones/${id}`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['vacaciones', accountId] }),
    })

    function ponerDia(key: keyof Plan, tramos: ReturnType<typeof tramosDeFranja>, punto?: { x: number; y: number }) {
        onCambiarPlan({ ...plan, [key]: franjaDeTramos(tramos) })
        onPreguntarAlcance(key, punto ?? { x: window.innerWidth / 2, y: window.innerHeight / 2 })
    }

    return (
        <article
            className={
                'flex h-full flex-col rounded-md border bg-neutral-950/60 ' +
                (seleccionada ? 'border-neutral-500 ring-2 ring-offset-1 ring-offset-neutral-950' : 'border-neutral-800')
            }
            style={{
                borderTopColor: color,
                borderTopWidth: 3,
                ['--tw-ring-color' as string]: color,
            }}
        >
            <header className="flex items-start justify-between gap-2 px-2 py-1.5">
                <button type="button" onClick={onSeleccionar} className="min-w-0 text-left">
                    {colocando && (
                        <p className="text-[10px] uppercase tracking-wide text-neutral-500">
                            Hueco {hueco} de {totalHuecos}
                        </p>
                    )}
                    <h3 className="truncate text-sm font-semibold text-neutral-100">{profesional.nombre}</h3>
                    <p className="text-[11px] leading-snug text-neutral-500">{resumenPlan(plan)}</p>
                    {vigente && (
                        <p className="text-[11px] text-amber-200/90">De baja {textoBaja(vigente.fin)}</p>
                    )}
                </button>
                {colocando && (
                    <div className="flex shrink-0 gap-1">
                        <button
                            type="button"
                            disabled={hueco === 1}
                            onClick={onSubir}
                            className="rounded border border-neutral-800 px-1.5 py-0.5 text-[11px] text-neutral-400 disabled:opacity-30"
                        >
                            ←
                        </button>
                        <button
                            type="button"
                            disabled={hueco === totalHuecos}
                            onClick={onBajar}
                            className="rounded border border-neutral-800 px-1.5 py-0.5 text-[11px] text-neutral-400 disabled:opacity-30"
                        >
                            →
                        </button>
                    </div>
                )}
            </header>

            <div className="flex-1 space-y-1 border-t border-neutral-800 px-2 py-1.5">
                {DIAS.map(({ key, corto, label }) => {
                    const tramos = tramosDeFranja(plan[key])
                    return (
                        <div
                            key={key}
                            className={
                                'space-y-1 rounded px-1 py-0.5 ' +
                                (seleccionada && diaSeleccionado === key ? 'bg-neutral-800/70' : '')
                            }
                        >
                            <div className="flex items-center gap-2">
                                <label className="flex w-12 shrink-0 items-center gap-1 text-[11px]">
                                    <input
                                        type="checkbox"
                                        checked={tramos.length > 0}
                                        aria-label={label}
                                        onChange={(e) =>
                                            ponerDia(
                                                key,
                                                e.target.checked ? [{ start: '09:00', end: '18:00' }] : [],
                                            )
                                        }
                                        className="h-3.5 w-3.5 rounded border-neutral-700 bg-neutral-900"
                                    />
                                    <span className={tramos.length > 0 ? 'text-neutral-200' : 'text-neutral-600'}>
                                        {corto}
                                    </span>
                                </label>
                                {tramos.length === 0 && (
                                    <span className="text-[11px] text-neutral-600">cerrado</span>
                                )}
                                {tramos.length === 1 && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const p = partirTramos(tramos)
                                                if (p) ponerDia(key, p)
                                            }}
                                            className="text-[10px] text-neutral-500 hover:text-neutral-300"
                                        >
                                            Partir
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => ponerDia(key, añadirTramo(tramos))}
                                            className="text-[10px] text-neutral-500 hover:text-neutral-300"
                                        >
                                            + tarde
                                        </button>
                                    </>
                                )}
                            </div>
                            {tramos.map((t, i) => (
                                <div key={`${t.start}-${i}`} className="flex items-center gap-1 pl-12">
                                    <input
                                        type="time"
                                        value={t.start}
                                        aria-label={`${label} tramo ${i + 1}: abre`}
                                        onChange={(e) => {
                                            const next = tramos.map((x, j) =>
                                                j === i ? { ...x, start: e.target.value } : x,
                                            )
                                            ponerDia(key, next)
                                        }}
                                        className="w-[4.6rem] rounded border border-neutral-800 bg-neutral-950 px-1 py-0.5 text-[11px]"
                                    />
                                    <span className="text-[10px] text-neutral-600">–</span>
                                    <input
                                        type="time"
                                        value={t.end}
                                        aria-label={`${label} tramo ${i + 1}: cierra`}
                                        onChange={(e) => {
                                            const next = tramos.map((x, j) =>
                                                j === i ? { ...x, end: e.target.value } : x,
                                            )
                                            ponerDia(key, next)
                                        }}
                                        className="w-[4.6rem] rounded border border-neutral-800 bg-neutral-950 px-1 py-0.5 text-[11px]"
                                    />
                                    {tramos.length > 1 && (
                                        <button
                                            type="button"
                                            aria-label={`Quitar tramo ${i + 1} de ${label}`}
                                            onClick={() => ponerDia(key, quitarTramo(tramos, i))}
                                            className="text-[10px] text-neutral-500 hover:text-rose-400"
                                        >
                                            quitar
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )
                })}
                {!soloLectura && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                    <button
                        type="button"
                        disabled={guardar.isPending}
                        onClick={() => guardar.mutate()}
                        className="rounded bg-sky-600 px-2.5 py-1 text-[11px] text-white disabled:opacity-50"
                    >
                        Guardar
                    </button>
                    {msg && <p className="text-[11px] text-neutral-400">{msg}</p>}
                </div>
                )}
            </div>

            <div className="space-y-2 border-t border-neutral-800 px-2 py-1.5">
                <div>
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                        <p className="text-[11px] font-medium text-neutral-400">De baja</p>
                        {!soloLectura && vigente && (
                            <button
                                type="button"
                                className="text-[11px] text-sky-300 hover:text-sky-200"
                                disabled={borrarVac.isPending}
                                onClick={() => borrarVac.mutate(vigente.id)}
                            >
                                Reactivar
                            </button>
                        )}
                    </div>
                    {!soloLectura && !vigente && (
                        <div className="space-y-1.5">
                            <div className="flex flex-wrap gap-1">
                                {(
                                    [
                                        ['hasta', 'Hasta una fecha'],
                                        ['indefinida', 'Hasta nuevo aviso'],
                                        ['empieza', 'Empieza el…'],
                                    ] as const
                                ).map(([m, et]) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => {
                                            setModoBaja(m)
                                            setErrorBaja(null)
                                        }}
                                        className={
                                            'rounded-md border px-2 py-0.5 text-[10px] ' +
                                            (modoBaja === m
                                                ? 'border-amber-400/50 bg-amber-500/15 text-amber-100'
                                                : 'border-neutral-800 text-neutral-500 hover:text-neutral-300')
                                        }
                                    >
                                        {et}
                                    </button>
                                ))}
                            </div>
                            {modoBaja && modoBaja !== 'indefinida' && (
                                <input
                                    type="date"
                                    value={fechaBaja}
                                    min={hoy}
                                    onChange={(e) => setFechaBaja(e.target.value)}
                                    className="w-full rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-[11px] text-neutral-100"
                                />
                            )}
                            {modoBaja && (
                                <button
                                    type="button"
                                    disabled={altaVac.isPending}
                                    onClick={() => {
                                        const r = intervaloBaja(
                                            fechaBaja
                                                ? { hoy, modo: modoBaja, fecha: fechaBaja }
                                                : { hoy, modo: modoBaja },
                                        )
                                        if ('error' in r) {
                                            setErrorBaja(r.error)
                                            return
                                        }
                                        altaVac.mutate(
                                            avisoCitas !== null ? { ...r, confirmarConCitas: true } : r,
                                        )
                                    }}
                                    className="rounded-md bg-amber-500/90 px-2.5 py-1 text-[11px] font-medium text-amber-950 hover:bg-amber-400 disabled:opacity-50"
                                >
                                    Confirmar baja{avisoCitas !== null ? ' igual' : ''}
                                </button>
                            )}
                            {errorBaja && <p className="text-[11px] text-rose-400">{errorBaja}</p>}
                        </div>
                    )}
                </div>
                <div>
                    <p className="mb-1.5 text-[11px] font-medium text-neutral-400">Vacaciones</p>
                    <div className="flex flex-wrap gap-1.5">
                        <input
                            type="date"
                            value={vacDesde}
                            onChange={(e) => setVacDesde(e.target.value)}
                            className="min-w-0 flex-1 rounded-md border border-neutral-700 bg-neutral-900 px-1.5 py-0.5 text-[11px]"
                        />
                        <input
                            type="date"
                            value={vacHasta}
                            onChange={(e) => setVacHasta(e.target.value)}
                            className="min-w-0 flex-1 rounded-md border border-neutral-700 bg-neutral-900 px-1.5 py-0.5 text-[11px]"
                        />
                        {!soloLectura && (
                        <button
                            type="button"
                            disabled={!vacDesde || !vacHasta || altaVac.isPending}
                            onClick={() =>
                                altaVac.mutate({
                                    inicio: `${vacDesde} 00:00:00`,
                                    fin: `${vacHasta} 23:59:59`,
                                    ...(avisoCitas !== null ? { confirmarConCitas: true } : {}),
                                })
                            }
                            className="rounded-md border border-amber-500/40 px-2 py-0.5 text-[11px] text-amber-200"
                        >
                            {avisoCitas !== null ? 'Anular igual' : '+'}
                        </button>
                        )}
                    </div>
                    {errorBaja && <p className="mt-1 text-[11px] text-rose-400">{errorBaja}</p>}
                    <ul className="mt-1.5 space-y-0.5">
                        {vacaciones.length === 0 && (
                            <li className="text-[11px] text-neutral-600">Sin bloqueos.</li>
                        )}
                        {vacaciones.map((v) => (
                            <li
                                key={v.id}
                                className="flex items-center justify-between gap-2 text-[11px] text-neutral-400"
                            >
                                <span>
                                    {esFinIndefinido(v.fin)
                                        ? `De baja ${textoBaja(v.fin)}`
                                        : `${v.inicio.slice(0, 10)} → ${v.fin.slice(0, 10)}`}
                                </span>
                                {!soloLectura && (
                                <button
                                    type="button"
                                    className="text-rose-400"
                                    onClick={() => borrarVac.mutate(v.id)}
                                >
                                    quitar
                                </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </article>
    )
}
